import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../UserContext';
import ToolCard from '../components/ToolCard';
import SearchBar from '../components/SearchBar';
import Sidebar from '../components/Sidebar';
import { Link } from 'react-router-dom';

const MyToolsPage = () => {
  const [myTools, setMyTools] = useState([]);
  const [filteredTools, setFilteredTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useUser();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setError("Please log in to view your tools.");
        setLoading(false);
        return;
      }

      try {
        // Fetch categories
        const categoriesCollection = collection(db, 'toolCategories');
        const categoriesSnapshot = await getDocs(categoriesCollection);
        const categoriesData = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          icon: doc.data().icon // Make sure to include the icon field
        }));
        setCategories(categoriesData);

        // Fetch tools
        const toolsCollection = collection(db, 'tools');
        const q = query(toolsCollection, where("ownerId", "==", user.uid));
        const q2 = query(toolsCollection, where("userId", "==", user.uid));
        const [toolsSnapshot, toolsSnapshot2] = await Promise.all([getDocs(q), getDocs(q2)]);
        
        const toolsList = [
          ...toolsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            categoryName: categoriesData.find(cat => cat.id === doc.data().category)?.name || 'Unknown Category',
            isOwner: true,
            available: doc.data().availability === 'true' // Convert string 'true' to boolean
          })),
          ...toolsSnapshot2.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            categoryName: categoriesData.find(cat => cat.id === doc.data().category)?.name || 'Unknown Category',
            isOwner: doc.data().ownerId === user.uid,
            available: doc.data().availability === 'true' // Convert string 'true' to boolean
          }))
        ];
        
        // Remove duplicates in case a tool has both ownerId and userId
        const uniqueToolsList = Array.from(new Set(toolsList.map(tool => tool.id)))
          .map(id => toolsList.find(tool => tool.id === id));
        
        setMyTools(uniqueToolsList);
        setFilteredTools(uniqueToolsList);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data: ", err);
        setError("Failed to load your tools. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSearch = (searchTerm) => {
    filterTools(searchTerm, selectedCategories, availabilityFilter);
  };

  const handleCategoryChange = (categoryId) => {
    const updatedCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(c => c !== categoryId)
      : [...selectedCategories, categoryId];
    setSelectedCategories(updatedCategories);
    filterTools('', updatedCategories, availabilityFilter);
  };

  const handleAvailabilityChange = (availability) => {
    setAvailabilityFilter(availability);
    filterTools('', selectedCategories, availability);
  };

  const filterTools = (searchTerm, categoryIds, availability) => {
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    const filtered = myTools.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(lowercasedSearchTerm) ||
        (tool.description && tool.description.toLowerCase().includes(lowercasedSearchTerm));
      
      const matchesCategory = categoryIds.length === 0 || categoryIds.includes(tool.category);

      const matchesAvailability = availability === 'all' ||
        (availability === 'available' && tool.available) ||
        (availability === 'unavailable' && !tool.available);

      return matchesSearch && matchesCategory && matchesAvailability;
    });
    setFilteredTools(filtered);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return <div className="text-center mt-8">Loading your tools...</div>;
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">{error}</div>;
  }

  const NoToolsMessage = () => (
    <div className="text-center mt-8">
      <p className="text-xl mb-4">You haven't added any tools yet.</p>
      <div className="relative inline-block">
        <p className="text-lg mb-2">Click the blue button to add your first tool!</p>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row">
        <button
          className="md:hidden mb-4 bg-blue-500 text-white px-4 py-2 rounded"
          onClick={toggleSidebar}
        >
          {sidebarOpen ? 'Close Filters' : 'Open Filters'}
        </button>
        <div className={`w-full md:w-1/4 ${sidebarOpen ? 'block' : 'hidden'} md:block`}>
          <Sidebar 
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryChange={handleCategoryChange}
            availabilityFilter={availabilityFilter}
            onAvailabilityChange={handleAvailabilityChange}
          />
        </div>
        <div className="w-full md:w-3/4 md:ml-4">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
            <h1 className="text-3xl font-bold mb-4 sm:mb-0">My Tools</h1>
            <Link to="/add-tool" className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded w-full sm:w-auto text-center">
              Add a tool
            </Link>
          </div>
          {myTools.length === 0 ? (
            <NoToolsMessage />
          ) : (
            <>
              <SearchBar onSearch={handleSearch} />
              {filteredTools.length === 0 ? (
                <p className="mt-4">No tools found matching your criteria.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                  {filteredTools.map(tool => (
                    <ToolCard key={tool.id} tool={tool} showEditButton={tool.isOwner} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyToolsPage;
